import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { GlobalStyles } from './styles';
import { useToast, Modal, Btn } from './components/UI';
import NotificationEnvelope from './components/NotificationEnvelope';
import { HeaderBackProvider, HeaderBack, HeaderBackButton, HeaderBackStyles, useActiveHeaderBack, getDefaultBack } from './components/HeaderBack';
import { setToken } from './api';
import { LastPathRecorder, LandingRedirect, clearLastPath } from './lastPath';
import * as api from './api';

import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectSetupPage from './pages/ProjectSetupPage';
import BOQPage from './pages/BOQPage';
import AdminBOQPage from './pages/AdminBOQPage';
import ProcurementPage from './pages/ProcurementPage';
import LabourPage from './pages/LabourPage';
import SiteExecutionPage from './pages/SiteExecutionPage';
import FinancePage from './pages/FinancePage';
import AnalyticsPage from './pages/AnalyticsPage';
import SiteLabourDashboardPage from './pages/SiteLabourDashboardPage';
import FinanceAnalyticsDashboardPage from './pages/FinanceAnalyticsDashboardPage';
import AlertsPage from './pages/AlertsPage';
import ProjectDivisionDetailsPage from './pages/ProjectDivisionDetailsPage';
import ComingSoonPage from './pages/ComingSoonPage';
import AdminPage from './pages/AdminPage';
import ProfilePage from './pages/ProfilePage';
import ForcePasswordChangePage from './pages/ForcePasswordChangePage';
import UserProfilePage from './pages/UserProfilePage';
import UserFormPage from './pages/UserFormPage';
import DMRDashboard from './pages/DMRDashboard';
import ReportEntryPage from './pages/ReportEntryPage';
import StandaloneDMREntryPage from './pages/StandaloneDMREntryPage';
import VerifyUserPage from './pages/VerifyUserPage';
import QSSiteInstallationPage from './pages/QSSiteInstallationPage';
import StandaloneQSEntryPage from './pages/StandaloneQSEntryPage';
import BulkQSEntryPage from './pages/BulkQSEntryPage';
import ExpenseMasterPage from './pages/ExpenseMasterPage';
import ExpenseRegisterPage from './pages/ExpenseRegisterPage';
import ExpenseMasterDMRPage from './pages/ExpenseMasterDMRPage';
import BOQHubPage from './pages/BOQHubPage';
import ExpenseMasterQSIHubPage from './pages/ExpenseMasterQSIHubPage';
import BOQEntryPage from './pages/BOQEntryPage';
import EnterpriseRequisitionPage from './pages/EnterpriseRequisitionPage';
import RequisitionDirectEdit from './components/requisition/RequisitionDirectEdit';
import PaymentVoucherEnginePage from './pages/PaymentVoucherEnginePage';
import MaterialRequestPage from './pages/MaterialRequestPage';
import MaterialTakeoffPage from './pages/MaterialTakeoffPage';
import PermissionGate from './components/PermissionGate';
import useMyPhoto from './hooks/useMyPhoto';
import { PERM } from './utils';
import MaterialPOPage from './pages/MaterialPOPage';
import { Routes, Route, useNavigate, useLocation, Navigate, useMatch } from 'react-router-dom';
import { syncDarkFixer } from './darkTheme';
import { NAV, NAV_ICONS, MODULE_ICONS, COMING_SOON_MODULES, canSeeNavItem, navItemPaths, navSubmenu } from './navConfig';
import ModuleHubPage from './pages/ModuleHubPage';
import HRApp from './hr/HRApp';
import PayrollApp from './payroll/PayrollApp';
import { payrollPageTitle } from './payroll/catalog';
import { HR_MODULE, hrGroupUrl, hrModuleUrl, hrPageTitle, findSubmodule as findHRSubmodule } from './hr/catalog';
import HRIcon from './hr/Icon';
// Any of these opens the edit-user form (each section is then enabled by its own tick).
const USER_EDIT_PERMS = ['admin_users:edit', 'admin_users:assign_roles', 'admin_users:deactivate', 'admin_users:mfa'];

const PAGE_LABELS = {
  dashboard: 'Dashboard',
  'hr-people': 'HR & People',
  payroll: 'Payroll & Compensation',
  'cost-control': 'Cost Control',
  'report-entry': 'Report Entry',
  'expense-master': 'Expense Master',
  'expense-master-expense-register': 'All-In-One Expense Register',
  'expense-master-boq-hub': 'Project Bill of Quantities',
  'boq-hub': 'Bill of Quantities',
  'report-entry-dmr': 'DMR Entry Form',
  'report-entry-qs': 'QS Installation Form',
  'report-entry-bulk-qs': 'QS Entry Form',
  'report-entry-boq': 'BOQ Entry Form',
  'qsi-hub': 'QS Site Installation',
  projects: 'Projects',
  alerts: 'Alerts',
  setup: 'Project Setup',
  boq: 'Bill of Quantities',
  boqbudgets: 'Project Budget',
  procurement: 'Procurement',
  labour: 'Labour & Timesheets',
  site: 'Site Execution',
  finance: 'Finance & Variations',
  analytics: 'Analytics & Reports',
  inventory: 'Inventory & Procurement',
  'enterprise-requisition': 'Enterprise Requisition Management',
  'payment-voucher-engine': 'Payment Voucher Engine',
  'material-takeoff': 'Material Take-Off & Planning',
  'material-request': 'Material Request',
  'material-purchase-orders': 'Material Purchase Orders',
  admin: 'System Administration',
  profile: 'My Profile',
  'direct-material': 'Direct Material Register',
  'site-labour': 'Site Labour Register',
  'qs-measurements': 'QS Measurements',
  'qs-site': 'QS Site Installation Register',
  'coming-soon': 'Coming Soon',
  ...Object.fromEntries(COMING_SOON_MODULES.map(m => [m.id, m.label])),
};

const MenuIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

// Pin (not pinned — click to pin) / PinOff (pinned — click to unpin)
const PinIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="17" x2="12" y2="22" />
    <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1v3.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
  </svg>
);
const UnpinIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="2" y1="2" x2="22" y2="22" />
    <line x1="12" y1="17" x2="12" y2="22" />
    <path d="M9 9v1.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V17h9" />
    <path d="M15 9.34V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H9.5" />
  </svg>
);

// One link in an expanded sidebar submenu (HR & People, Cost Control, Expense Master, Inventory …)
function SubmenuLink({ label, icon, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 10px', margin: '1px 0',
      background: active ? 'rgba(255,255,255,0.13)' : 'none', color: active ? '#fff' : 'var(--sidebar-text-secondary)',
      border: 'none', borderRadius: 10, fontSize: 12.5, fontWeight: active ? 700 : 600, cursor: 'pointer', fontFamily: 'var(--font)', textAlign: 'left',
    }}
      onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'var(--sidebar-bg-hover)'; e.currentTarget.style.color = '#fff'; } }}
      onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--sidebar-text-secondary)'; } }}
    >
      {icon && <span style={{ width: 14, height: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</span>}
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
    </button>
  );
}

const submenuFrame = { display: 'flex', flexDirection: 'column', margin: '2px 0 8px 22px', borderLeft: '1px solid rgba(255,255,255,0.12)', paddingLeft: 10 };

// HR & People submenu: one link per submenu, from the HR catalog
function HRSubmenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const parts = location.pathname.split('/').filter(Boolean);
  const activeGroup = parts[1] === 'g' ? parts[2] : parts[1] === 's' ? findHRSubmodule(parts[2])?.group.key : null;
  return (
    <div style={submenuFrame}>
      {HR_MODULE.groups.map(g => (
        <SubmenuLink key={g.key} label={g.title} icon={<HRIcon name={g.icon} size={14} />} active={activeGroup === g.key} onClick={() => navigate(hrGroupUrl(g.key))} />
      ))}
    </div>
  );
}

// any other module's submenu: its hub children or its own pages (navSubmenu in navConfig)
function ModuleSubmenu({ links }) {
  const navigate = useNavigate();
  const location = useLocation();
  const here = decodeURIComponent(location.pathname + location.search);
  return (
    <div style={submenuFrame}>
      {links.map(l => {
        const active = l.to.includes('?') ? here === l.to : location.pathname.startsWith(l.to);
        const svg = MODULE_ICONS[l.id]?.svg;
        const icon = l.lucide ? <HRIcon name={l.lucide} size={14} /> : svg ? React.cloneElement(svg, { width: 14, height: 14 }) : null;
        return <SubmenuLink key={l.id} label={l.label} icon={icon} active={active} onClick={() => navigate(l.to)} />;
      })}
    </div>
  );
}

function Sidebar({ user, alertCount, collapsed, setCollapsed, pinned, onPinToggle, lockedClosed, onLockClosed, onUnlockOpen, mobileMenuOpen }) {
  const myPhoto = useMyPhoto(user); // own HR photo for the avatar, initials when there is none
  const navigate = useNavigate();
  const location = useLocation();
  const logoH = 70;

  // Unpinned + collapsed sidebar temporarily reveals on hover, and gracefully
  // collapses back (via the existing width transition) once the mouse leaves —
  // unless it's been locked closed (double-click), which stays steady regardless of hover.
  const [isHovering, setIsHovering] = useState(false);
  // Modules with sub-pages (HR & People, Cost Control, Expense Master, Inventory …) expand into a
  // submenu; the one you are inside opens by itself and stays open
  const menuOwner = NAV.flatMap(g => g.items)
    .find(item => (item.submenu || item.children || item.links) && navItemPaths(item).some(id => location.pathname.startsWith(`/${id}`)))?.id;
  const [openMenus, setOpenMenus] = useState(() => (menuOwner ? { [menuOwner]: true } : {}));
  useEffect(() => { if (menuOwner) setOpenMenus(m => (m[menuOwner] ? m : { ...m, [menuOwner]: true })); }, [menuOwner]);
  const effectiveCollapsed = lockedClosed || (!pinned && collapsed && !isHovering);

  // A plain click collapses/unpins; a double-click locks it closed instead. Both
  // fire on the same button, so the single-click action is deferred briefly —
  // if a second click arrives in that window, it's a double-click and the
  // deferred single-click action is cancelled.
  const clickTimerRef = useRef(null);
  const handleMenuClick = () => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      return;
    }
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      if (pinned) {
        onPinToggle();
      } else {
        setCollapsed(c => !c);
      }
    }, 250);
  };
  const handleMenuDoubleClick = () => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    onLockClosed();
  };

  return (
    <div
      className={`app-sidebar ${mobileMenuOpen ? 'sidebar-mobile open' : 'hide-mobile'}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      style={{
        width: effectiveCollapsed ? 76 : 256, flexShrink: 0,
        height: 'var(--real-vh, 100vh)',
        position: 'sticky', top: 0,
        background: '#071e53ff',
        display: 'flex', flexDirection: 'column',
        transition: 'width 0.24s cubic-bezier(0.22,1,0.36,1)', overflow: 'hidden',
        boxShadow: '1px 0 0 var(--sidebar-border)',
      }}>
      {/* soft glow at the top of the rail */}
      <div style={{ position: 'absolute', top: -120, left: -40, width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle, rgba(34,211,238,0.30), transparent 65%)', filter: 'blur(20px)', pointerEvents: 'none' }} />

      {/* Logo */}
      <div style={{
        height: logoH, flexShrink: 0, position: 'relative',
        padding: effectiveCollapsed ? '0' : '0 16px 0 20px',
        borderBottom: '1px solid var(--sidebar-border)',
        display: 'flex', alignItems: 'center',
        justifyContent: effectiveCollapsed ? 'center' : 'space-between', gap: 10,
      }}>
        {!effectiveCollapsed ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <img src="/rnl-logo.png" alt="Rifitel" style={{ width: 38, height: 38, borderRadius: 11, flexShrink: 0, boxShadow: '0 8px 20px rgba(0,0,0,0.35)' }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15.5, fontWeight: 800, color: 'var(--sidebar-text-primary)', letterSpacing: '-0.03em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Rifitel Nigeria</div>
              <div style={{ fontSize: 8, fontWeight: 800, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.12em', whiteSpace: 'nowrap' }}>BUSINESS CENTRAL</div>
            </div>
          </div>
        ) : (
          <img src="/rnl-logo.png" alt="Rifitel" style={{ width: 36, height: 36, borderRadius: 10 }} />
        )}
        {!effectiveCollapsed && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
            <button onClick={handleMenuClick} onDoubleClick={handleMenuDoubleClick} title="Collapse menu (double-click to lock it collapsed)" style={{
              background: 'none', border: 'none', color: 'var(--sidebar-text-secondary)', cursor: 'pointer',
              padding: 6, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.15s, color 0.15s', flexShrink: 0,
            }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--sidebar-bg-hover)'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--sidebar-text-secondary)'; }}
            ><MenuIcon /></button>
            <button onClick={onPinToggle} title={pinned ? 'Unpin sidebar' : 'Pin sidebar open'} style={{
              background: 'none', border: 'none', color: pinned ? '#fff' : 'var(--sidebar-text-secondary)', cursor: 'pointer',
              padding: 6, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.15s, color 0.15s', flexShrink: 0,
            }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--sidebar-bg-hover)'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = pinned ? '#fff' : 'var(--sidebar-text-secondary)'; }}
            >{pinned ? <UnpinIcon /> : <PinIcon />}</button>
          </div>
        )}
      </div>

      {/* Locked-closed rail keeps just the menu icon, so it can be reopened without hovering */}
      {lockedClosed && (
        <button onClick={onUnlockOpen} title="Expand menu" style={{
          margin: '10px auto 0', background: 'none', border: 'none', color: 'var(--sidebar-text-secondary)',
          cursor: 'pointer', padding: 8, borderRadius: 9, display: 'flex',
        }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--sidebar-bg-hover)'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
        ><MenuIcon /></button>
      )}

      {/* Nav */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '14px 12px', position: 'relative' }}>
        {NAV.map(g => {
          const visibleItems = g.items.filter(item => canSeeNavItem(user, item));
          if (visibleItems.length === 0) return null;
          return (
            <div key={g.group} style={{ marginBottom: 16 }}>
              {!effectiveCollapsed && (
                <div style={{ padding: '6px 12px 8px', fontSize: 10, fontWeight: 800, color: 'var(--sidebar-text-muted)', letterSpacing: '0.16em' }}>{g.group}</div>
              )}
              {visibleItems.map(item => {
                const searchParams = new URLSearchParams(location.search);
                const isAdminRoute = location.pathname.startsWith('/admin') || searchParams.get('admin') === 'true' || location.state?.inAdminMode;
                let active = navItemPaths(item).some(id => location.pathname.startsWith(`/${id}`)) || (item.id === 'dashboard' && location.pathname === '/');
                if (item.id === 'expense-master' && location.pathname.startsWith('/expense-master/boq-hub')) {
                  active = false;
                }
                if (isAdminRoute) {
                  active = item.id === 'admin';
                }
                const subLinks = item.submenu === 'hr' ? null : navSubmenu(user, item);
                const isSubmenu = item.submenu === 'hr' || subLinks.length > 0;
                const isOpen = !!openMenus[item.id];
                const onItemClick = () => {
                  if (!isSubmenu) return navigate(`/${item.id}`);
                  // the first click opens the module's page and unfolds its submenu, a second click folds it
                  if (!isOpen || effectiveCollapsed) {
                    navigate(item.submenu === 'hr' ? hrModuleUrl() : `/${item.id}`);
                    setOpenMenus(m => ({ ...m, [item.id]: true }));
                  } else setOpenMenus(m => ({ ...m, [item.id]: false }));
                  return null;
                };
                return (
                  <React.Fragment key={item.id}>
                  <button onClick={onItemClick} title={effectiveCollapsed ? item.label : undefined} style={{
                    position: 'relative',
                    display: 'flex', alignItems: 'center', gap: 12,
                    width: '100%', padding: effectiveCollapsed ? '11px 0' : '10px 13px',
                    margin: '3px 0',
                    justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
                    background: active ? 'rgba(255,255,255,0.13)' : 'none',
                    color: active ? '#ffffff' : 'var(--sidebar-text-secondary)',
                    border: 'none', borderRadius: 12,
                    fontSize: 13.5, fontWeight: active ? 700 : 600,
                    cursor: 'pointer', fontFamily: 'var(--font)',
                    transition: 'background 0.15s, color 0.15s',
                    boxShadow: active ? 'inset 0 0 0 1px rgba(255,255,255,0.08), 0 6px 18px -8px rgba(0,0,0,0.5)' : 'none',
                  }}
                    onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'var(--sidebar-bg-hover)'; e.currentTarget.style.color = '#fff'; } }}
                    onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--sidebar-text-secondary)'; } }}
                  >
                    {active && <span style={{ position: 'absolute', left: effectiveCollapsed ? 8 : 4, top: '50%', transform: 'translateY(-50%)', width: 4, height: 18, borderRadius: 4, background: 'linear-gradient(180deg, var(--brand-2), #fff)' }} />}
                    <span style={{ width: 20, height: 20, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {NAV_ICONS[item.id] || <span style={{ fontSize: 14 }}>{item.icon}</span>}
                    </span>
                    {!effectiveCollapsed && (
                      <>
                        <span style={{ flex: 1, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
                        {item.badge && alertCount > 0 && (
                          <span style={{ background: '#ef4444', color: '#fff', borderRadius: 999, fontSize: 10, fontWeight: 800, padding: '2px 7px', lineHeight: '14px', fontVariantNumeric: 'tabular-nums' }}>{alertCount}</span>
                        )}
                      </>
                    )}
                    {effectiveCollapsed && item.badge && alertCount > 0 && (
                      <span style={{ position: 'absolute', top: 8, right: 14, width: 8, height: 8, borderRadius: '50%', background: '#ef4444' }} />
                    )}
                    {isSubmenu && !effectiveCollapsed && <HRIcon name={isOpen ? 'chevron-down' : 'chevron-right'} size={15} style={{ flexShrink: 0, opacity: 0.8 }} />}
                  </button>
                  {isSubmenu && isOpen && !effectiveCollapsed && (item.submenu === 'hr' ? <HRSubmenu /> : <ModuleSubmenu links={subLinks} />)}
                  </React.Fragment>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* User footer */}
      {user && !effectiveCollapsed && (
        <div style={{ padding: '14px 16px', borderTop: '1px solid var(--sidebar-border)', display: 'flex', alignItems: 'center', gap: 11 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 11, flexShrink: 0,
            background: 'linear-gradient(135deg, var(--brand), var(--brand-2))', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 14, boxShadow: '0 8px 18px -6px var(--brand)', overflow: 'hidden',
          }}>
            {myPhoto ? <img src={myPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : (user.first_name?.[0] || user.email?.[0] || '?').toUpperCase()}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--sidebar-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user.first_name} {user.last_name}
            </div>
            <div style={{ fontSize: 11, color: 'var(--sidebar-text-secondary)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user.role?.display_name || user.role?.role_name || user.email}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// TOP BAR  (light)
// ─────────────────────────────────────────────
function TopBar({ page, selected, user, onLogout, theme, setTheme, onMenuClick, alertCount, onExitImpersonation, token }) {
  const myPhoto = useMyPhoto(user); // own HR photo for the avatar, initials when there is none
  const navigate = useNavigate();
  const location = useLocation();
  // inside HR & People the bar shows the sub-module or submenu title, as the reference layout does
  const pageLabel = hrPageTitle(location.pathname) || payrollPageTitle(location.pathname) || PAGE_LABELS[page] || 'Dashboard';
  const [userPopupOpen, setUserPopupOpen] = React.useState(false);
  const [userPopupPos, setUserPopupPos] = React.useState(null);   // where the account popup opens, under the avatar
  const showChip = selected && ['setup', 'boq', 'procurement', 'labour', 'site', 'finance', 'analytics'].includes(page);

  // The view's own Back wins; otherwise step up one level of the route.
  const registeredBack = useActiveHeaderBack();
  const defaultBack = getDefaultBack(location.pathname);
  const back = registeredBack
    ? { label: registeredBack.label, onClick: registeredBack.onClick, disabled: registeredBack.disabled }
    : defaultBack
      ? { label: defaultBack.label, onClick: () => navigate(defaultBack.to) }
      : null;

  return (
    <div className="topbar-container" style={{
      height: 70, flexShrink: 0, position: 'relative',
      background: '#031540ff', borderBottom: '1px solid #213342',
      display: 'flex', alignItems: 'center', padding: '0 26px', gap: 14,
    }}>
      <HeaderBackStyles />
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0, zIndex: 2 }}>
        <button className="hide-desktop" onClick={onMenuClick} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 4 }}>
          <MenuIcon />
        </button>

        {back && <HeaderBackButton label={back.label} onClick={back.onClick} disabled={back.disabled} />}

        {/* Impersonation bar. Deliberately loud and always in the same place:
            everything done from here is recorded against the person named,
            so it must never be possible to forget whose account this is. */}
        {user?.impersonator_id && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: '#b45309', border: '1px solid #f59e0b', borderRadius: 999,
            padding: '5px 6px 5px 14px', whiteSpace: 'nowrap', zIndex: 2,
            boxShadow: '0 2px 10px rgba(0,0,0,0.35)',
          }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: '#fff' }}>
              Logged in as {user.first_name} {user.last_name}
            </span>
            <button
              onClick={onExitImpersonation}
              title={`Return to ${user.impersonator_name || 'your own account'}`}
              style={{
                background: '#fff', color: '#b45309', border: 'none', borderRadius: 999,
                padding: '4px 12px', fontSize: 12, fontWeight: 800, cursor: 'pointer',
                fontFamily: 'var(--font)',
              }}
            >
              Log out
            </button>
          </div>
        )}
      </div>

      <div className="topbar-title-container" style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
        <span className="topbar-title-text" style={{ fontSize: 19, fontWeight: 800, color: '#ffffff', letterSpacing: '-0.03em', whiteSpace: 'nowrap' }}>
          {pageLabel}
        </span>
        {showChip && (
          <span className="hide-mobile" style={{
            fontSize: 11, fontWeight: 700, color: 'var(--accent)',
            background: 'var(--accent-dim)', padding: '4px 11px', borderRadius: 999,
            fontFamily: 'var(--mono)', letterSpacing: '0.02em',
          }}>
            {selected?.project_code || selected?.project_name || '—'}
          </span>
        )}
      </div>

      <div className="hide-mobile" style={{ flex: 1 }} />

      {/* Envelope then bell. Two independent feeds: this one is about the
          reader's own work, the bell beside it is about project health. */}
      <NotificationEnvelope token={token} />

      <button
        onClick={() => navigate('/alerts')}
        title="Alerts"
        style={{
          position: 'relative',
          background: '#0a141c', border: '1px solid #213342', borderRadius: 11,
          padding: '8px 10px', color: '#9eb1bd', fontSize: 12.5, fontWeight: 700,
          cursor: 'pointer', fontFamily: 'var(--font)', display: 'flex', alignItems: 'center',
          transition: 'background 0.15s, border-color 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = '#1c3140'; e.currentTarget.style.borderColor = '#2d4456'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#0a141c'; e.currentTarget.style.borderColor = '#213342'; }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
        </svg>
        {alertCount > 0 && (
          <span style={{
            position: 'absolute', top: -6, right: -6,
            background: '#ef4444', color: '#fff', borderRadius: 999,
            fontSize: 10, fontWeight: 800, padding: '1px 5px', minWidth: 16, textAlign: 'center',
            lineHeight: '14px', fontVariantNumeric: 'tabular-nums',
            border: '2px solid #031540ff',
          }}>{alertCount > 99 ? '99+' : alertCount}</span>
        )}
      </button>

      <button
        onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
        title="Toggle theme"
        style={{
          background: '#0a141c', border: '1px solid #213342', borderRadius: 11,
          width: 36, height: 36, padding: 0, color: '#9eb1bd',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background 0.15s, border-color 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = '#1c3140'; e.currentTarget.style.borderColor = '#2d4456'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#0a141c'; e.currentTarget.style.borderColor = '#213342'; }}
      >
        {theme === 'light'
          ? (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" /></svg>)
          : (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>)}
      </button>

      {user && (
        <div style={{ position: 'relative' }}>
          <button
            onClick={e => {
              const r = e.currentTarget.getBoundingClientRect();
              setUserPopupPos({ top: r.bottom + 8, right: window.innerWidth - r.right });
              setUserPopupOpen(o => !o);
            }}
            title="Account"
            style={{
              width: 36, height: 36, borderRadius: 11, border: 'none', cursor: 'pointer',
              background: 'linear-gradient(135deg, var(--brand), var(--brand-2))', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, fontSize: 14, flexShrink: 0, boxShadow: '0 8px 18px -7px var(--brand)',
              outline: userPopupOpen ? '2px solid #60a5fa' : 'none', overflow: 'hidden', padding: 0,
            }}
          >
            {myPhoto ? <img src={myPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : (user.first_name?.[0] || user.email?.[0] || '?').toUpperCase()}
          </button>
          {/* rendered into <body>: inside the top bar it was capped by the bar's stacking layer and the page cards covered it */}
          {userPopupOpen && userPopupPos && createPortal(
            <>
              <div onClick={() => setUserPopupOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1999 }} />
              <div style={{
                position: 'fixed', top: userPopupPos.top, right: userPopupPos.right, zIndex: 2000,
                background: '#0a1f3d', border: '1px solid #213342', borderRadius: 12,
                padding: '14px 18px', minWidth: 200, boxShadow: '0 8px 32px rgba(0,0,0,.45)',
              }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 4 }}>
                  {user.first_name} {user.last_name}
                </div>
                <div style={{ fontSize: 12, color: '#9eb1bd' }}>{user.email}</div>
              </div>
            </>,
            document.body
          )}
        </div>
      )}

      <div style={{ width: 1, height: 28, background: '#213342' }} />

      <button
        onClick={onLogout}
        title="Sign out"
        className="mobile-p-10"
        style={{
          background: '#ef4444', border: 'none',
          borderRadius: 6, padding: '8px 16px',
          color: '#ffffff', fontSize: 13, fontWeight: 600,
          cursor: 'pointer', fontFamily: 'var(--font)',
          transition: 'background 0.2s',
          display: 'flex', alignItems: 'center', gap: 6
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#dc2626'}
        onMouseLeave={e => e.currentTarget.style.background = '#ef4444'}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
        <span className="hide-mobile">Sign Out</span>
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────
// APP ROOT  (logic identical to original)
// ─────────────────────────────────────────────
export default function App() {
  const [token, setTok] = useState(() => localStorage.getItem('pf_token') || null);
  const [refresh, setRefresh] = useState(() => localStorage.getItem('pf_refresh') || null);
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  // Sidebar defaults open. "Pinned" (saved in localStorage) means it stays open;
  // unpinning it collapses it to a narrow rail that only hover reveals (see Sidebar).
  // "Locked closed" (double-click the menu icon) is the opposite: stays collapsed
  // permanently, ignoring hover, until the rail's menu icon is clicked to reopen it.
  const [pinned, setPinned] = useState(() => {
    const stored = localStorage.getItem('pf_sidebar_pinned');
    return stored === null ? true : stored === 'true';
  });
  const [lockedClosed, setLockedClosed] = useState(() => localStorage.getItem('pf_sidebar_locked_closed') === 'true');
  const [collapsed, setCollapsed] = useState(() => (lockedClosed ? true : !pinned));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [autoLogoutWarning, setAutoLogoutWarning] = useState(false);
  const [countdown, setCountdown] = useState(30);

  const navigate = useNavigate();
  const location = useLocation();

  const getLogicalPage = () => {
    const path = location.pathname;
    if (path === '/' || path.startsWith('/dashboard')) return 'dashboard';
    if (path.startsWith('/report-entry/dmr')) return 'report-entry-dmr';
    if (path.startsWith('/report-entry/bulk-qs')) return 'report-entry-bulk-qs';
    if (path.startsWith('/report-entry/qs')) return 'report-entry-qs';
    if (path.startsWith('/report-entry/boq')) return 'report-entry-boq';
    if (path.startsWith('/qsi-hub')) return 'qsi-hub';
    if (path.startsWith('/report-entry')) return 'report-entry';
    if (path.startsWith('/expense-master/boq-hub')) return 'expense-master-boq-hub';
    if (path.startsWith('/expense-master/expense-register')) return 'expense-master-expense-register';
    if (path.startsWith('/projects')) {
      const parts = path.split('/').filter(Boolean);
      if (parts[3] === 'boqbudgets') return 'boqbudgets';
      if (parts.length >= 3) return parts[2];
      return 'projects';
    }
    if (path.startsWith('/admin/projects')) {
      const parts = path.split('/').filter(Boolean);
      if (parts[4] === 'boqbudgets') return 'boqbudgets';
      if (parts.length >= 4) return parts[3];
      return 'admin-projects';
    }
    const base = path.split('/')[1];
    return base || 'dashboard';
  };
  const page = getLogicalPage();
  const isProjectDivisionDetail = useMatch('/projects/:projectId/project-divisions/:projectDivisionId');

  // Saved both to localStorage (instant on next load) and the user's DB record
  // (survives a cleared cache / follows them to another device or browser).
  const persistSidebarPreference = (payload) => {
    if (!token) return;
    api.updateMe(payload).catch(() => {}); // best-effort — localStorage still has it
  };

  const handlePinToggle = () => {
    const next = !pinned;
    setPinned(next);
    localStorage.setItem('pf_sidebar_pinned', String(next));
    const payload = { sidebar_pinned: next };
    if (next) {
      setLockedClosed(false);
      localStorage.setItem('pf_sidebar_locked_closed', 'false');
      payload.sidebar_locked_closed = false;
    }
    setCollapsed(!next);
    persistSidebarPreference(payload);
  };

  const handleLockClosed = () => {
    setLockedClosed(true);
    localStorage.setItem('pf_sidebar_locked_closed', 'true');
    setPinned(false);
    localStorage.setItem('pf_sidebar_pinned', 'false');
    setCollapsed(true);
    persistSidebarPreference({ sidebar_locked_closed: true, sidebar_pinned: false });
  };

  const handleUnlockOpen = () => {
    setLockedClosed(false);
    localStorage.setItem('pf_sidebar_locked_closed', 'false');
    setCollapsed(false);
    persistSidebarPreference({ sidebar_locked_closed: false });
  };

  useEffect(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    if (parts[0] === 'projects' && parts[1]) {
      const urlProjectId = parts[1];
      const p = projects.find(x => x.project_id === urlProjectId);
      if (p && (!selected || selected.project_id !== p.project_id)) {
        setSelected(p);
      }
    }
  }, [location.pathname, projects, selected]);
  const [theme, setTheme] = useState(() => localStorage.getItem('pf_theme') || 'light');
  // The container now lives in ToastProvider at the root — rendering one
  // here too would stack every toast twice.
  const { show: showToast } = useToast();

  useEffect(() => {
    if (!token) {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.setAttribute('data-theme', theme);
      localStorage.setItem('pf_theme', theme);
    }
    // dark mode re-colours screens that set light colours inline (src/darkTheme.js); off in light mode
    syncDarkFixer(token ? theme : 'light'); // the sign-in page is designed dark already
  }, [theme, token]);

  const toast = { show: showToast };

  // Held in memory only, never persisted: it is the password the user typed
  // at sign-in, kept just long enough to pass to the change-password call so
  // they are not asked to retype a password an administrator gave them.
  const [tempPassword, setTempPassword] = useState(null);

  const handleLogin = useCallback((accessToken, refreshToken, opts = {}) => {
    setTok(accessToken);
    setRefresh(refreshToken);
    setToken(accessToken);
    localStorage.setItem('pf_token', accessToken);
    localStorage.setItem('pf_refresh', refreshToken);
    if (opts.mustChangePassword) setTempPassword(opts.currentPassword || null);
  }, []);

  // Clears the gate below without a round trip to /users/me.
  const handlePasswordChanged = useCallback(() => {
    setTempPassword(null);
    setUser(u => (u ? { ...u, must_change_password: false } : u));
    showToast('Your password has been changed.');
  }, []); // eslint-disable-line

  // Leaving an impersonation is a local restore of the administrator's parked
  // token, not a fresh sign-in. The server call only closes the audit window,
  // so a failure there must not strand the admin in the other account — hence
  // the restore happens regardless.
  const [showExitImpersonation, setShowExitImpersonation] = useState(false);
  const [exitingImpersonation, setExitingImpersonation] = useState(false);

  const exitImpersonation = useCallback(async () => {
    setExitingImpersonation(true);
    try {
      await api.stopImpersonation();
    } catch (e) {
      // Recorded server-side or not, the admin still gets their session back.
    }
    const restored = api.restoreAdminSession();
    if (!restored) {
      // Nothing parked — treat it as a normal sign-out rather than leaving
      // them holding a token for somebody else's account.
      setToken(null);
      localStorage.removeItem('pf_token');
      localStorage.removeItem('pf_refresh');
      window.location.href = '/';
      return;
    }
    api.clearApiCache();
    window.location.href = '/';
  }, []);

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const handleLogoutClick = useCallback(() => { setShowLogoutConfirm(true); }, []);

  const confirmLogout = useCallback(() => {
    setShowLogoutConfirm(false);
    setTok(null); setRefresh(null); setUser(null); setProjects([]); setSelected(null);
    setToken(null);
    localStorage.removeItem('pf_token');
    localStorage.removeItem('pf_refresh');
    // A parked administrator session would otherwise survive a full sign-out
    // and be restored by the next person to use the browser.
    api.clearAdminSession();
    clearLastPath();
    window.location.href = '/';
  }, []);

  // ─────────────────────────────────────────────
  // INACTIVITY AUTO-LOGOUT (30 mins)
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;

    let warningTimeoutId;
    let countdownIntervalId;
    const INACTIVITY_WARNING_TIME = (30 * 60 * 1000) - (30 * 1000); // 29.5 minutes

    const resetTimer = () => {
      if (warningTimeoutId) clearTimeout(warningTimeoutId);
      if (countdownIntervalId) clearInterval(countdownIntervalId);

      setAutoLogoutWarning(false);
      setCountdown(30);

      warningTimeoutId = setTimeout(() => {
        setAutoLogoutWarning(true);
        let currentCount = 30;
        setCountdown(currentCount);

        countdownIntervalId = setInterval(() => {
          currentCount -= 1;
          setCountdown(currentCount);
          if (currentCount <= 0) {
            clearInterval(countdownIntervalId);
            confirmLogout();
            toast.show('You have been logged out due to inactivity.', 'error');
            setAutoLogoutWarning(false);
          }
        }, 1000);
      }, INACTIVITY_WARNING_TIME);
    };

    let throttleTimer;
    const handleActivity = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        resetTimer();
        throttleTimer = null;
      }, 1000); // Throttle event handling to max once per second
    };

    const events = ['mousemove', 'keydown', 'mousedown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));
    resetTimer(); // Initialize timer

    return () => {
      events.forEach(e => window.removeEventListener(e, handleActivity));
      if (warningTimeoutId) clearTimeout(warningTimeoutId);
      if (countdownIntervalId) clearInterval(countdownIntervalId);
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [token, confirmLogout]); // eslint-disable-line

  useEffect(() => {
    if (!token) return;
    setToken(token);
    const boot = async () => {
      try {
        const [u, p] = await Promise.all([
          api.getMe(),
          api.listProjects(),
          new Promise(r => setTimeout(r, 3500)) // Ensure spinner shows while loading
        ]);
        setUser(u);
        // The DB is the cross-device source of truth for the sidebar preference —
        // localStorage is just a cache for instant paint before this resolves.
        // A null field means the user never saved a preference there yet, so the
        // localStorage-derived value (already applied at mount) stands.
        if (u?.sidebar_pinned != null || u?.sidebar_locked_closed != null) {
          const nextPinned = u.sidebar_pinned != null ? u.sidebar_pinned : pinned;
          const nextLockedClosed = u.sidebar_locked_closed != null ? u.sidebar_locked_closed : lockedClosed;
          setPinned(nextPinned);
          setLockedClosed(nextLockedClosed);
          setCollapsed(nextLockedClosed ? true : !nextPinned);
          localStorage.setItem('pf_sidebar_pinned', String(nextPinned));
          localStorage.setItem('pf_sidebar_locked_closed', String(nextLockedClosed));
        }
        const ps = Array.isArray(p) ? p : [];
        setProjects(ps);
        if (ps.length > 0 && !selected) {
          const active = ps.find(x => x.status === 'active') || ps[0];
          setSelected(active);
        }
      } catch {
        confirmLogout();
      }
    };
    boot();
    const handleUnauthorized = () => confirmLogout();
    window.addEventListener('unauthorized', handleUnauthorized);
    return () => window.removeEventListener('unauthorized', handleUnauthorized);
  }, [token]); // eslint-disable-line

  useEffect(() => {
    if (!token) return;
    const load = async () => {
      try {
        const ps = await api.listProjects();
        const projectsList = Array.isArray(ps) ? ps : [];
        const counts = await Promise.all(projectsList.map(p => api.getAlertCount(p.project_id).catch(() => ({ unread_count: 0 }))));
        const totalUnread = counts.reduce((sum, c) => sum + (c?.unread_count || 0), 0);
        setAlertCount(totalUnread);
      } catch { }
    };
    load();
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, [token]);

  if (!token) {
    return (
      <>
        <GlobalStyles />
        <Routes>
          <Route path="/" element={<LoginPage onLogin={handleLogin} theme={theme} setTheme={setTheme} />} />
          <Route path="/auth/verify" element={<VerifyUserPage theme={theme} setTheme={setTheme} />} />
          <Route path="*" element={<LoginPage onLogin={handleLogin} theme={theme} setTheme={setTheme} />} />
        </Routes>
      </>
    );
  }

  if (!user) {
    return (
      <>
        <GlobalStyles />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18, alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#3a5085ff', color: 'var(--text-muted)' }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 240, height: 240, marginBottom: 12 }}>
            <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', animation: 'spin 1.2s linear infinite' }} viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="46" fill="none" stroke="#000000ff" strokeWidth="4" />
              <circle cx="50" cy="50" r="46" fill="none" stroke="#fcfdfdff" strokeWidth="4" strokeDasharray="144.5" strokeLinecap="round" />
            </svg>
            <img src="/rnl-logo.png" alt="Rifitel" style={{ width: 150, height: 150, borderRadius: 40, animation: 'pulse 1.6s ease-in-out infinite', boxShadow: 'var(--shadow)', position: 'relative', zIndex: 1 }} />
          </div>
          <style>{`
            @keyframes pulse{0%,100%{opacity:0.45;transform:scale(0.98)}50%{opacity:1;transform:scale(1)}}
            @keyframes spin{to{transform:rotate(360deg)}}
          `}</style>
          <div style={{ fontSize: 28.5, letterSpacing: '0.02em', fontWeight: 'bold', color: 'black' }}>Loading workspace…</div>
        </div>
      </>
    );
  }

  // Placed above the whole authenticated shell rather than as a route, so
  // there is no URL that reaches the app around it. The flag is read from
  // /users/me, so a reload lands back here instead of slipping through.
  if (user.must_change_password) {
    return (
      <>
        <GlobalStyles />
        <ForcePasswordChangePage
          user={user}
          currentPassword={tempPassword}
          onDone={handlePasswordChanged}
          onLogout={confirmLogout}
        />
      </>
    );
  }

  return (
    <HeaderBackProvider>
      <GlobalStyles />
      <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>

        {mobileMenuOpen && (
          <div className="sidebar-overlay" onClick={() => setMobileMenuOpen(false)} />
        )}

        <Sidebar user={user} alertCount={alertCount} collapsed={collapsed} setCollapsed={setCollapsed} pinned={pinned} onPinToggle={handlePinToggle} lockedClosed={lockedClosed} onLockClosed={handleLockClosed} onUnlockOpen={handleUnlockOpen} mobileMenuOpen={mobileMenuOpen} />

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, position: 'relative' }}>

          {/* Watermark */}
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.04, overflow: 'hidden' }}>
            <img src="/rnl-logo.png" alt="Watermark" style={{ width: '150%', height: '150%', objectFit: 'contain' }} />
          </div>

          <div style={{ position: 'relative', zIndex: 1 }}>
            <TopBar page={page} selected={selected} user={user} onLogout={handleLogoutClick} theme={theme} setTheme={setTheme} onMenuClick={() => setMobileMenuOpen(true)} alertCount={alertCount} onExitImpersonation={() => setShowExitImpersonation(true)} token={token} />
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: 28, position: 'relative', zIndex: 1 }}>
            {['project-divisions', 'boq', 'boqbudgets', 'procurement', 'labour', 'site', 'site-issues', 'direct-material', 'qs-site', 'finance', 'analytics', 'coming-soon'].includes(page) && selected && !isProjectDivisionDetail && (
              <div id="global-project-back-bar" className="flex-mobile-col" style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'var(--bg-card)', border: '1px solid black', borderRadius: 14, boxShadow: 'var(--shadow-sm)', backgroundColor: '#e4ebefdf' }}>
                <HeaderBack
                  label="Back to Project Dashboard"
                  onClick={() => {
                    const searchParams = new URLSearchParams(location.search);
                    const isAdminRoute = location.pathname.startsWith('/admin/projects');
                    const isAdminMode = searchParams.get('admin') === 'true' || location.state?.inAdminMode || isAdminRoute;
                    if (isAdminMode) {
                      let url = `/admin?tab=projects&id=${selected.project_id}`;
                      if (page === 'direct-material') url += '&category=DMR';
                      if (page === 'labour') url += '&category=LABOUR';
                      navigate(url);
                    } else {
                      let group = page;
                      if (page === 'project-divisions' || page === 'projectDivisions') group = 'project-divisions';
                      if (page === 'boqbudgets') group = 'boq';
                      if (page === 'labour' || page === 'site' || page === 'site-issues' || page === 'direct-material' || page === 'qs-site') group = 'site';
                      if (page === 'finance' || page === 'analytics') group = 'finance';
                      navigate(`/projects/${selected.project_id}?group=${group}`);
                    }
                  }}
                />
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  Currently managing: <strong style={{ color: 'var(--text-primary)' }}>{selected.project_name}</strong>
                </span>
              </div>
            )}

            <LastPathRecorder />
            <Routes>
              <Route path="/login" element={<Navigate to="/dashboard" />} />
              {/* Returning to the bare domain reopens the last page, not
                  always the dashboard. Falls back to /dashboard when there is
                  nothing usable stored. */}
              <Route path="/" element={<LandingRedirect />} />
              <Route path="/dashboard" element={<DashboardPage user={user} />} />
              <Route path="/cost-control" element={<ModuleHubPage user={user} parentId="cost-control" />} />
              <Route path="/report-entry" element={<ReportEntryPage toast={toast} />} />
              <Route path="/report-entry/dmr" element={<StandaloneDMREntryPage toast={toast} />} />
              <Route path="/report-entry/qs" element={<StandaloneQSEntryPage toast={toast} />} />
              <Route path="/report-entry/bulk-qs" element={<BulkQSEntryPage toast={toast} />} />
              <Route path="/report-entry/boq" element={<BOQEntryPage toast={toast} />} />
              <Route path="/report-entry/coming-soon" element={<ComingSoonPage />} />
              <Route path="/expense-master" element={<ExpenseMasterPage toast={toast} user={user} />} />
              <Route path="/expense-master/expense-register" element={<ExpenseRegisterPage toast={toast} />} />
              <Route path="/boq-hub/*" element={<BOQHubPage toast={toast} user={user} />} />
              <Route path="/expense-master/dmr" element={<ExpenseMasterDMRPage toast={toast} />} />
              <Route path="/qsi-hub" element={<ExpenseMasterQSIHubPage toast={toast} />} />
              <Route path="/expense-master/coming-soon" element={<ComingSoonPage />} />
              <Route path="/inventory" element={<ModuleHubPage user={user} parentId="inventory" />} />
              <Route path="/hr-people/*" element={<HRApp user={user} toast={toast} />} />
              <Route path="/payroll/*" element={<PayrollApp user={user} toast={toast} />} />
              {/* Settings moved into System Administration; old links land on its card there */}
              <Route path="/settings" element={<Navigate to="/admin?tab=settings" replace />} />
              {COMING_SOON_MODULES.map(m => (
                <Route key={m.id} path={`/${m.id}`} element={<ComingSoonPage moduleName={m.label} />} />
              ))}
              {/* Every tab is addressable. The bare path still resolves —
                  it is what the sidebar link and older bookmarks point at —
                  and the page falls back to the dashboard for it. */}
              <Route path="/enterprise-requisition" element={<PermissionGate user={user} perm={PERM.ENTERPRISE_REQUISITION_VIEW} module="Enterprise Requisition"><EnterpriseRequisitionPage /></PermissionGate>} />
              <Route path="/enterprise-requisition/:tab" element={<PermissionGate user={user} perm={PERM.ENTERPRISE_REQUISITION_VIEW} module="Enterprise Requisition"><EnterpriseRequisitionPage /></PermissionGate>} />
              {/* Sub-views inside a tab — cc-queue/load, new/:dept/drafts. */}
              <Route path="/enterprise-requisition/:tab/*" element={<PermissionGate user={user} perm={PERM.ENTERPRISE_REQUISITION_VIEW} module="Enterprise Requisition"><EnterpriseRequisitionPage /></PermissionGate>} />
              {/* Full-page edit of a finished requisition. A route rather than a
                  tab because it is reached from a row, and going "back" should
                  mean the list rather than whichever tab was last open. */}
              <Route path="/enterprise-requisition/edit/:prId" element={<PermissionGate user={user} perm={PERM.ENTERPRISE_REQUISITION_VIEW} module="Enterprise Requisition"><RequisitionDirectEdit /></PermissionGate>} />
              {/* Same addressing as the requisition module: a tab per url,
                  and a splat for the views nested inside one. */}
              <Route path="/payment-voucher-engine" element={<PaymentVoucherEnginePage toast={toast} />} />
              <Route path="/payment-voucher-engine/:tab" element={<PaymentVoucherEnginePage toast={toast} />} />
              <Route path="/payment-voucher-engine/:tab/*" element={<PaymentVoucherEnginePage toast={toast} />} />
              <Route path="/material-takeoff" element={<MaterialTakeoffPage />} />
              <Route path="/material-takeoff/:tab" element={<MaterialTakeoffPage />} />
              <Route path="/material-takeoff/:tab/:id" element={<MaterialTakeoffPage />} />
              <Route path="/material-request" element={<PermissionGate user={user} perm={PERM.MATERIAL_REQUEST_VIEW} module="Material Request"><MaterialRequestPage /></PermissionGate>} />
              <Route path="/material-request/:tab" element={<PermissionGate user={user} perm={PERM.MATERIAL_REQUEST_VIEW} module="Material Request"><MaterialRequestPage /></PermissionGate>} />
              <Route path="/material-request/:tab/*" element={<PermissionGate user={user} perm={PERM.MATERIAL_REQUEST_VIEW} module="Material Request"><MaterialRequestPage /></PermissionGate>} />
              <Route path="/material-purchase-orders" element={<PermissionGate user={user} perm={PERM.MATERIAL_PO_VIEW} module="Material Purchase Orders"><MaterialPOPage user={user} /></PermissionGate>} />
              <Route path="/material-purchase-orders/:tab" element={<PermissionGate user={user} perm={PERM.MATERIAL_PO_VIEW} module="Material Purchase Orders"><MaterialPOPage user={user} /></PermissionGate>} />
              <Route path="/material-purchase-orders/:tab/*" element={<PermissionGate user={user} perm={PERM.MATERIAL_PO_VIEW} module="Material Purchase Orders"><MaterialPOPage user={user} /></PermissionGate>} />
              <Route path="/alerts" element={<AlertsPage toast={toast} />} />

              <Route path="/projects" element={<ProjectsPage toast={toast} />} />
              <Route path="/projects/:projectId" element={<ProjectsPage toast={toast} />} />

              <Route path="/projects/:projectId/project-divisions/:projectDivisionId" element={<ProjectDivisionDetailsPage toast={toast} />} />
              <Route path="/projects/:projectId/project-divisions" element={<ProjectSetupPage toast={toast} />} />
              <Route path="/projects/:projectId/boq/:tab" element={<BOQPage toast={toast} />} />
              <Route path="/projects/:projectId/boq" element={<BOQPage toast={toast} />} />
              <Route path="/projects/:projectId/procurement/:tab" element={<ProcurementPage toast={toast} />} />
              <Route path="/projects/:projectId/procurement" element={<ProcurementPage toast={toast} />} />
              <Route path="/projects/:projectId/labour/:tab" element={<LabourPage toast={toast} />} />
              <Route path="/projects/:projectId/labour" element={<LabourPage toast={toast} />} />
              <Route path="/projects/:projectId/site-issues/:tab" element={<SiteExecutionPage toast={toast} />} />
              <Route path="/projects/:projectId/site-issues" element={<SiteExecutionPage toast={toast} />} />
              <Route path="/projects/:projectId/site-labour" element={<SiteLabourDashboardPage toast={toast} />} />
              <Route path="/projects/:projectId/direct-material/:tab" element={<DMRDashboard toast={toast} />} />
              <Route path="/projects/:projectId/direct-material" element={<DMRDashboard toast={toast} />} />
              <Route path="/projects/:projectId/qs-site" element={<QSSiteInstallationPage toast={toast} />} />

              <Route path="/projects/:projectId/finance/:tab" element={<FinancePage toast={toast} />} />
              <Route path="/projects/:projectId/finance" element={<Navigate to="variations" replace />} />
              <Route path="/projects/:projectId/analytics/:tab" element={<AnalyticsPage toast={toast} />} />
              <Route path="/projects/:projectId/analytics" element={<Navigate to="cost" replace />} />
              <Route path="/projects/:projectId/finance-analytics" element={<FinanceAnalyticsDashboardPage toast={toast} />} />
              <Route path="/projects/:projectId/coming-soon" element={<ComingSoonPage />} />

              <Route path="/admin" element={<AdminPage toast={toast} user={user} />} />
              <Route path="/admin/user-access" element={<AdminPage toast={toast} user={user} />} />
              <Route path="/admin/user-access/:section" element={<AdminPage toast={toast} user={user} />} />
              {/* The literal segments are declared before the parameterised
                  one so "new" is never read as a user id. */}
              <Route path="/admin/users/new" element={<PermissionGate user={user} perm="admin_users:create" module="Create User"><UserFormPage mode="create" /></PermissionGate>} />
              <Route path="/admin/users/:userId/edit" element={<PermissionGate user={user} anyOf={USER_EDIT_PERMS} module="Edit User"><UserFormPage mode="edit" /></PermissionGate>} />
              <Route path="/admin/users/:userId" element={<PermissionGate user={user} perm="admin_users:view" module="User Profiles"><UserProfilePage toast={toast} currentUser={user} /></PermissionGate>} />
              <Route path="/admin/projects/:projectId/project-divisions/:projectDivisionId" element={<ProjectDivisionDetailsPage toast={toast} />} />
              <Route path="/admin/projects/:projectId/project-divisions" element={<ProjectSetupPage toast={toast} />} />
              <Route path="/admin/projects/:projectId/boq/:tab" element={<AdminBOQPage toast={toast} />} />
              <Route path="/admin/projects/:projectId/boq" element={<AdminBOQPage toast={toast} />} />
              <Route path="/profile" element={<ProfilePage user={user} toast={toast} onLogout={handleLogoutClick} />} />
              <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
          </div>
        </div>
      </div>


      {autoLogoutWarning && (
        <Modal onClose={() => setAutoLogoutWarning(false)} width={400} hideHeader>
          <div style={{ textAlign: 'center', padding: '30px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h3 style={{ fontSize: 20, fontWeight: 800, marginBottom: 10, color: 'var(--text-primary)' }}>Session Expiring</h3>
            <p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 30, padding: '0 10px', lineHeight: 1.5 }}>
              You will be automatically logged out due to inactivity in:
            </p>

            <div style={{ position: 'relative', width: 140, height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="140" height="140" viewBox="0 0 140 140" style={{ transform: 'rotate(-90deg)', position: 'absolute' }}>
                <circle cx="70" cy="70" r="64" fill="none" stroke="var(--border)" strokeWidth="8" />
                <circle
                  cx="70" cy="70" r="64"
                  fill="none"
                  stroke="var(--brand)"
                  strokeWidth="8"
                  strokeLinecap="round"
                  style={{
                    strokeDasharray: 2 * Math.PI * 64,
                    strokeDashoffset: (2 * Math.PI * 64) * (1 - countdown / 30),
                    transition: 'stroke-dashoffset 1s linear'
                  }}
                />
              </svg>
              <div style={{ position: 'relative', fontSize: 38, fontWeight: 800, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                {countdown}
              </div>
            </div>

            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 30 }}>
              Move your mouse or press any key to stay logged in.
            </p>
          </div>
        </Modal>
      )}

      {showExitImpersonation && (
        <Modal onClose={exitingImpersonation ? () => { } : () => setShowExitImpersonation(false)} width={460} hideHeader>
          <div style={{ textAlign: 'center', padding: '10px 10px 0' }}>
            <div style={{
              width: 66, height: 66, background: 'var(--amber-dim)', borderRadius: 20,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 26px', color: 'var(--amber)'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
            </div>
            <h4 style={{ fontSize: 20, fontWeight: 800, marginBottom: 10, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
              Leave {user?.first_name} {user?.last_name}'s account
            </h4>
            <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 30, padding: '0 8px' }}>
              You'll be returned to {user?.impersonator_name || 'your own account'}.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Btn variant="ghost" disabled={exitingImpersonation} onClick={() => setShowExitImpersonation(false)} size="md" style={{ padding: '11px 0', fontSize: 14, fontWeight: 700 }}>Stay</Btn>
            <Btn variant="primary" loading={exitingImpersonation} onClick={exitImpersonation} size="md" style={{ padding: '11px 0', fontSize: 14, fontWeight: 700, background: 'linear-gradient(180deg, var(--amber), color-mix(in srgb, var(--amber) 80%, #000))', color: '#fff', border: 'none', boxShadow: '0 8px 20px -6px var(--amber)' }}>
              {exitingImpersonation ? 'Returning…' : 'Return to my account'}
            </Btn>
          </div>
        </Modal>
      )}

      {showLogoutConfirm && (
        <Modal onClose={() => setShowLogoutConfirm(false)} width={460} hideHeader>
          <div style={{ textAlign: 'center', padding: '10px 10px 0' }}>
            <div style={{
              width: 66, height: 66, background: 'var(--red-dim)', borderRadius: 20,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 26px', color: 'var(--red)'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
            </div>
            <h4 style={{ fontSize: 20, fontWeight: 800, marginBottom: 10, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>Sign out</h4>
            <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 30, padding: '0 8px' }}>
              You'll need to sign in again to get back to your workspace.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Btn variant="ghost" onClick={() => setShowLogoutConfirm(false)} size="md" style={{ padding: '11px 0', fontSize: 14, fontWeight: 700 }}>Cancel</Btn>
            <Btn variant="primary" onClick={confirmLogout} size="md" style={{ padding: '11px 0', fontSize: 14, fontWeight: 700, background: 'linear-gradient(180deg, var(--red), color-mix(in srgb, var(--red) 80%, #000))', color: '#fff', border: 'none', boxShadow: '0 8px 20px -6px var(--red)' }}>Sign out</Btn>
          </div>
        </Modal>
      )}
    </HeaderBackProvider>
  );
}
